import * as React from "react";
import { useForm } from "react-yup";
import styled from "styled-components";
import * as Yup from "yup";

import { Analytics, CustomError, PageEnvelope, SvgSprite } from "@skv/core";

import { Alert, AlertProps } from "react-bootstrap";
import { useUserState } from "~/state";
import { SplitField } from "~/ui/Form";

const SCHEMA = Yup.object({
  email: Yup.string()
    .email("Please enter a valid email address.")
    .required("Please enter an email address."),
}).defined();

const AccountWrapper = styled.section`
  @media (min-width: ${(p) => p.theme.lgBreakpoint}) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
`;

const SignOutWrapper = styled.section`
  @media (min-width: ${(p) => p.theme.lgBreakpoint}) {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
`;
const SignOutDiv = styled.div`
  border-bottom: 1px solid #e1e1e1;
  z-index: 1200;
  @media (min-width: ${(p) => p.theme.lgBreakpoint}) {
    position: absolute;
    top: 5rem;
    border-bottom: none;
    z-index: 1200;
  }
`;

const SignInCard = styled.div`
  border-bottom: 1px solid #e1e1e1;
  border-radius: 0px;
  @media (min-width: ${(p) => p.theme.lgBreakpoint}) {
    position: absolute !important;
    top: 3rem;
    width: 376px;
    box-shadow: 0 2px 6px 0 rgba(28, 28, 28, 0.5);
    z-index: 1200;
  }
`;

interface SignInPostViewModel {
  email: string;
  emailSent: boolean;
}

export const SignInOutPanel = (): JSX.Element => {
  const { email, isAuthenticated } = useUserState();

  const alertRef = React.useRef<HTMLDivElement>(null);
  const formRef = React.useRef(null);

  const {
    values,
    errors,
    touched,
    createSubmitHandler,
    field,
    FormProvider,
    isSubmitting,
    setSubmitting,
    setTouched,
  } = useForm({
    validationSchema: SCHEMA,
  });

  const [emailData, setEmailData] = React.useState(() => ({
    email: null,
    emailSent: false,
  }));

  const [serverError, setServerError] = React.useState<AlertProps>({
    show: false,
    variant: null,
    children: null,
  });

  const handleSubmit = React.useMemo(() => {
    return createSubmitHandler((v) => {
      setServerError((prev) => ({ ...prev, show: false }));

      fetch("/signin", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(v),
      })
        .then<PageEnvelope<SignInPostViewModel>>((res) => {
          if (res.ok) {
            return res.json();
          }

          throw Error("Response not ok");
        })
        .then(({ success, data, errorCode, errorMessage }) => {
          if (success) {
            setEmailData({
              email: data.email,
              emailSent: data.emailSent,
            });
            return;
          }

          switch (errorCode) {
            case "error:signin":
              throw new CustomError("ERROR", errorCode);
            default:
              throw Error(
                "Response ok, but server did not respond with a known error code"
              );
          }
        })
        .catch((err: Error | CustomError) => {
          // if (err instanceof CustomError) {
          //   setServerError(true);
          // }
          setServerError((prev) => ({
            ...prev,
            show: true,
            variant: "danger",
            children: <>An unexpected error has occurred. Please try again.</>,
          }));
        });
      setSubmitting(false);
    });
  }, [createSubmitHandler, setSubmitting]);

  return (
    <AccountWrapper>
      {isAuthenticated ? (
        <SignOutWrapper>
          <SignOutDiv>
            <form
              action="/signout"
              method="POST"
              className="my-4 mt-lg-n3 mb-lg-0"
              noValidate
            >
              <SvgSprite
                id="icons:envelope"
                width={16}
                height={16}
                className="mr-2"
              />
              <strong
                className="d-inline-block align-middle text-truncate mr-3"
                css={`
                  max-width: 250px;
                `}
              >
                {email}
              </strong>
              <button type="submit" className="btn btn-primary btn-sm">
                Sign out
              </button>
            </form>
          </SignOutDiv>
        </SignOutWrapper>
      ) : (
        <SignInCard id="signin-card" className="card">
          <div className="card-body px-0 px-lg-3">
            {emailData.emailSent ? (
              <div>
                <h2 className="h3">Email sent</h2>
                <p className="small mb-2">We have sent a sign-in email to:</p>
                <p className="small font-weight-bold mb-0">
                  <SvgSprite
                    id="icons:envelope"
                    width={12}
                    height={12}
                    className="mr-1 align-baseline"
                  />
                  {emailData.email}
                </p>
                <Analytics
                  data={{
                    subSection: "volunteers",
                    subSubSection: "signin",
                    subSubSubSection: "emailsent",
                    pageTitle: "signin",
                    pageType: "form",
                    loginStatus: "loggedout",
                  }}
                />
              </div>
            ) : (
              <>
                <h2>Sign in</h2>
                <p>
                  To view your email alerts and activity.
                  <br />
                  We will send you an email to confirm your email address.
                </p>
                <FormProvider>
                  <form
                    ref={formRef}
                    id="login-form"
                    action="/signin"
                    method="POST"
                    onSubmit={handleSubmit}
                    noValidate
                  >
                    <Alert
                      ref={alertRef}
                      dismissible
                      onClose={(e) => {
                        setServerError((prev) => ({
                          ...prev,
                          show: false,
                        }));
                      }}
                      {...serverError}
                    />
                    <SplitField
                      {...field}
                      id="login-input"
                      label="Email address"
                      name="email"
                      type="email"
                      autoComplete="email"
                      value={values.email || ""}
                      touched={!!touched.email}
                      error={errors.email}
                      onChange={(e) => {
                        field.onChange(e);
                        setTouched((prev) => ({ ...prev, email: false }), true);
                      }}
                      onBlur={(e) => {
                        field.onBlur(e);
                        setTouched(
                          (prev) => ({ ...prev, email: false }),
                          false
                        );
                      }}
                      append={
                        <button
                          className="flex-shrink-0 btn btn-primary"
                          type="submit"
                          id="login-btn"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? "Sending..." : "Send email"}
                        </button>
                      }
                    />
                  </form>
                </FormProvider>
              </>
            )}
          </div>
        </SignInCard>
      )}
    </AccountWrapper>
  );
};
